import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { FaCheckCircle, FaChevronDown, FaChevronUp, FaClock, FaTimesCircle } from "react-icons/fa";
import { formatDate, formatToZMW, rootUrl } from "../../../helpers";
import { useAuth } from "../../../context/auth-context";

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "0", label: "Pending Checking" },
  { value: "1", label: "Pending Authorization" },
  { value: "2", label: "Pending Approval" },
  { value: "3", label: "Approved" },
  { value: "9", label: "Declined" },
  { value: "4", label: "Reversed" },
];

const badgeClass = (status) => {
  const s = Number(status);
  if (s === 3) return "bg-green-100 text-green-700";
  if (s === 9) return "bg-red-100 text-red-700";
  if (s === 4) return "bg-gray-200 text-gray-700";
  return "bg-amber-100 text-amber-700";
};

export default function PvStatus({ startDate, endDate, triggerFetch }) {
  const auth = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [auditFilter, setAuditFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});

  const fetchData = async () => {
    if (!startDate || !endDate) return;
    try {
      setLoading(true);
      const payload = `${auth.user.id}~${startDate}~${endDate}`;
      const res = await axios.get(rootUrl(`/pvStatus/${payload}`));
      setRows(res.data?.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not load PV status report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (triggerFetch && startDate && endDate) {
      fetchData();
    }
  }, [triggerFetch]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      const statusOk = statusFilter === "all" ? true : String(r.status) === statusFilter;
      const term = search.trim().toLowerCase();
      const searchOk =
        !term ||
        String(r.voucher || "").toLowerCase().includes(term) ||
        String(r.cheque || "").toLowerCase().includes(term) ||
        String(r.description || "").toLowerCase().includes(term) ||
        String(r.payee_name || "").toLowerCase().includes(term);
      const auditOk =
        auditFilter === "all"
          ? true
          : auditFilter === "audited"
          ? Boolean(r.audited_by)
          : auditFilter === "not_audited"
          ? !Boolean(r.audited_by)
          : auditFilter === "mine"
          ? Boolean(r.audited_by_me)
          : true;
      return statusOk && searchOk && auditOk;
    });
  }, [rows, statusFilter, auditFilter, search]);

  const summary = useMemo(() => {
    const base = { total: rows.length, pending: 0, approved: 0, declined: 0 };
    rows.forEach((r) => {
      const s = Number(r.status);
      if ([0, 1, 2].includes(s)) base.pending += 1;
      if (s === 3) base.approved += 1;
      if (s === 9) base.declined += 1;
    });
    return base;
  }, [rows]);

  const toggleExpanded = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) {
    return <p className="text-sm text-gray-600 py-8">Loading PV approval status report...</p>;
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <SummaryCard title="Total PVs" value={summary.total} icon={<FaClock />} color="bg-blue-50 text-blue-800" />
        <SummaryCard title="Pending" value={summary.pending} icon={<FaClock />} color="bg-amber-50 text-amber-800" />
        <SummaryCard title="Approved" value={summary.approved} icon={<FaCheckCircle />} color="bg-green-50 text-green-800" />
        <SummaryCard title="Declined" value={summary.declined} icon={<FaTimesCircle />} color="bg-red-50 text-red-800" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          placeholder="Search voucher, cheque, payee or description..."
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          value={auditFilter}
          onChange={(e) => setAuditFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="all">All audit states</option>
          <option value="audited">Audited only</option>
          <option value="not_audited">Not audited</option>
          <option value="mine">Audited by me</option>
        </select>
        <div className="text-sm text-gray-600 flex items-center">
          Showing <span className="font-semibold mx-1">{filtered.length}</span> of {rows.length} PVs
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="min-w-full">
          <thead>
            <tr className="bg-blue-950 text-white text-left">
              <th className="px-3 py-2 text-xs">Voucher</th>
              <th className="px-3 py-2 text-xs">Date</th>
              <th className="px-3 py-2 text-xs">Payee</th>
              <th className="px-3 py-2 text-xs">Amount</th>
              <th className="px-3 py-2 text-xs">Current Status</th>
              <th className="px-3 py-2 text-xs">Pending At</th>
              <th className="px-3 py-2 text-xs">Audit</th>
              <th className="px-3 py-2 text-xs">Prepared By</th>
              <th className="px-3 py-2 text-xs">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <React.Fragment key={row.id}>
                <tr className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2 text-sm font-medium">{row.voucher || "-"}</td>
                  <td className="px-3 py-2 text-sm">{row.date ? formatDate(String(row.date).substring(0, 10)) : "-"}</td>
                  <td className="px-3 py-2 text-sm">{row.payee_name || "-"}</td>
                  <td className="px-3 py-2 text-sm">{formatToZMW(Number(row.amount) || 0)}</td>
                  <td className="px-3 py-2 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${badgeClass(row.status)}`}>
                      {row.status_text}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-sm">{row.pending_role || "-"}</td>
                  <td className="px-3 py-2 text-sm">
                    {row.audited_by ? (
                      <span className="text-red-700 font-semibold">
                        Audited by {row.audited_by}
                      </span>
                    ) : (
                      <span className="text-gray-500">Not audited</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-sm">{row.prepared_by || "-"}</td>
                  <td className="px-3 py-2 text-sm">
                    <button
                      type="button"
                      onClick={() => toggleExpanded(row.id)}
                      className="text-blue-800 hover:underline inline-flex items-center gap-1"
                    >
                      {expanded[row.id] ? <FaChevronUp /> : <FaChevronDown />}
                      Trail
                    </button>
                  </td>
                </tr>
                {expanded[row.id] && (
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <td colSpan={9} className="px-3 py-3">
                      <p className="text-xs font-semibold text-gray-700 mb-2">Approval trail</p>
                      {row.trail?.length ? (
                        <div className="space-y-2">
                          {row.trail.map((t) => (
                            <div key={t.id} className="border border-gray-200 rounded-lg bg-white px-3 py-2 text-xs">
                              <p className="font-medium text-gray-800">
                                Step {Number(t.hierarchy) + 1} - {`${t.firstname || ""} ${t.lastname || ""}`.trim() || "Unknown"}
                              </p>
                              <p className="text-gray-500">{t.created_at || ""}</p>
                              <p className="text-gray-700">{t.comment || "No comment"}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500 italic">No approvals recorded yet.</p>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-8 text-center text-sm text-gray-500">
                  No PV records found for selected filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SummaryCard({ title, value, icon, color }) {
  return (
    <div className={`rounded-xl border border-gray-200 p-3 ${color}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase">{title}</p>
        <div>{icon}</div>
      </div>
      <p className="text-2xl font-bold mt-2">{value}</p>
    </div>
  );
}
